import { createTheme } from "@mui/material";

// Theme construction for the tracking page — separate module so both the live app (App.js)
// and the /preview route can build a brand theme without a circular import.

function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '25, 118, 210';
}

// Mix a hex color towards white (0..1) — used to derive primary.light from the brand accent.
function lightenHex(hex, amt) {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return hex;
    const ch = (h) => Math.round(parseInt(h, 16) + (255 - parseInt(h, 16)) * amt).toString(16).padStart(2, '0');
    return `#${ch(m[1])}${ch(m[2])}${ch(m[3])}`;
}

// Normalize a brand accent to a 6-digit hex. Accepts 3-digit shorthand (#0af) and 6-digit, with or
// without '#', tolerating whitespace and non-string values — the SAME inputs getReadableAccent
// (trackingTemplateUtils) accepts, so the Classic theme and the Modern/Minimal palettes can never
// disagree on whether a configured colour is branded. Anything else → null (default theme).
function normalizeAccentHex(accent) {
    const raw = String(accent == null ? '' : accent).trim();
    const m3 = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(raw);
    if (m3) return `#${m3[1]}${m3[1]}${m3[2]}${m3[2]}${m3[3]}${m3[3]}`;
    const m6 = /^#?([a-f\d]{6})$/i.exec(raw);
    if (m6) return `#${m6[1]}`;
    return null;
}

// ONE brand knob: the accent colour (config's bannerBgColor — the same single colour Modern/Minimal
// already derive their whole palette from) also drives the Classic page's theme primary.
// success/error stay semantic green/red.
function deriveColorsFromAccent(accent) {
    const main = normalizeAccentHex(accent);
    if (!main) return null;
    return { primary: { main, light: lightenHex(main, 0.35) } };
}

// Built at RUNTIME from the resolved brand's colors (hardcoded domainDetails entry OR the DIY
// config's themeColors — same shape: {primary:{main,light}, success:{main,light}, background:{gradient}}).
// Legacy brands get their colors on first paint (the static entry is AppContext's initial brand);
// DIY-configured brands get theirs as soon as the config loads.
const buildTheme = (domainColors) => createTheme({
  typography: {
    fontFamily: 'Poppins, sans-serif',
    h6: {
      '@media (max-width:900px)': {
        fontSize: '1.1rem',
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
  palette: {
    primary: domainColors?.primary || {
      main: '#1565C0',
      light: '#42a5f5',
    },
    success: domainColors?.success || {
      main: '#2e7d32',
      light: '#4caf50',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    '0 2px 6px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
    '0 4px 12px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.04)',
    '0 6px 16px rgba(0,0,0,0.08), 0 3px 6px rgba(0,0,0,0.05)',
    '0 8px 24px rgba(0,0,0,0.09), 0 4px 8px rgba(0,0,0,0.05)',
    '0 12px 32px rgba(0,0,0,0.1), 0 6px 12px rgba(0,0,0,0.05)',
    ...Array(18).fill('0 12px 32px rgba(0,0,0,0.1), 0 6px 12px rgba(0,0,0,0.05)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          '--color-primary-rgb': hexToRgb(domainColors?.primary?.main || '#1565C0'),
          background: domainColors?.background?.gradient || 'linear-gradient(180deg, #f8f9fb 0%, #eceef2 100%)',
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(0,0,0,0.08)',
          transition: 'border-color 0.2s ease',
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '& fieldset': {
              borderColor: 'rgba(0,0,0,0.12)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0,0,0,0.24)',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

export { buildTheme, deriveColorsFromAccent };
